import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileSearch, Sparkles, Loader2, ChevronRight, Send, Clock, BellRing, Trash2, Power,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── helpers ──────────────────────────────────────────────────────────────────
const prettyIntent = (i) => {
  const map = {
    count_calls: 'Call count',
    missed_calls: 'Missed calls',
    repeat_callers: 'Repeat callers',
    top_callers: 'Top callers',
    avg_score_by_agent: 'Average score by agent',
    avg_score_by_call_type: 'Average score by call type',
    revivable_deals: 'Revivable deals',
    score_below_threshold: 'Calls below score threshold',
    no_transcript: 'No-transcript calls',
  };
  return map[i] || i;
};

const formatCell = (v) => {
  if (v == null || v === '') return '—';
  if (typeof v === 'string' && v.length > 60) return v.slice(0, 60) + '…';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    try { return new Date(v).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return v; }
  }
  return String(v);
};

const formatTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString(); }
  catch { return iso; }
};

// ── Digest button (inside result card header) ────────────────────────────────
const DigestButton = ({ result, apiPrefix, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [schedule, setSchedule] = useState('weekly_monday');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/reports/digest${apiPrefix}`, {
        question: result.question,
        schedule,
        subject_hint: result.question.slice(0, 80),
      });
      toast.success('Digest scheduled — you\'ll get an email at the next run.');
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save digest');
    } finally { setBusy(false); }
  };

  return (
    <div className="relative" data-testid="digest-btn-wrap">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-teal-200 bg-teal-50 hover:bg-teal-100 text-teal-800 inline-flex items-center gap-1.5"
        data-testid="digest-btn"
        title="Email this report to me on a schedule"
      >
        <BellRing className="w-3 h-3" /> Digest to my inbox
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-30 w-64 bg-white rounded-xl border border-slate-200 shadow-xl p-3"
          data-testid="digest-popover"
        >
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Send this report</p>
          <select
            value={schedule}
            onChange={(e) => setSchedule(e.target.value)}
            className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
            data-testid="digest-schedule-select"
          >
            <option value="daily_weekday">Daily (Mon–Fri)</option>
            <option value="weekly_monday">Weekly (Mondays)</option>
            <option value="monthly_1st">Monthly (1st of month)</option>
          </select>
          <p className="text-[10px] text-slate-400 mt-1">Delivered around 7:00 UTC to your login email.</p>
          <div className="flex justify-end gap-1.5 mt-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[11px] font-semibold text-slate-600 px-2 py-1 rounded hover:bg-slate-100"
            >Cancel</button>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="text-[11px] font-semibold bg-teal-600 text-white px-2 py-1 rounded hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="digest-save"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />}
              Save digest
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


// ── Result table ─────────────────────────────────────────────────────────────
const ResultTable = ({ result, apiPrefix, onDigestSaved }) => {
  if (!result) return null;
  const { columns = [], rows = [], total, intent, spec, extra = {}, generated_at } = result;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="report-result">
      <CardContent className="pt-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600">Result</p>
            <p className="text-sm text-slate-700 mt-0.5">
              <span className="font-semibold">{prettyIntent(intent)}</span>
              <span className="text-slate-400 mx-2">·</span>
              <span className="text-slate-500">last {spec?.days ?? 30} days</span>
              <span className="text-slate-400 mx-2">·</span>
              <span className="tabular-nums">{total} match{total === 1 ? '' : 'es'}</span>
              {extra.threshold_used != null && (
                <>
                  <span className="text-slate-400 mx-2">·</span>
                  <span className="text-slate-500">threshold {extra.threshold_used}</span>
                </>
              )}
            </p>
            {spec?.notes && (
              <p className="text-[11px] italic text-slate-400 mt-1">AI interpretation: {spec.notes}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <DigestButton result={result} apiPrefix={apiPrefix} onSaved={onDigestSaved} />
            <p className="text-[11px] text-slate-400">generated {formatTime(generated_at)}</p>
          </div>
        </div>

        {rows.length === 0 ? (
          <p className="text-center text-slate-400 text-sm py-8">No rows to show.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-100">
            <table className="min-w-full text-sm" data-testid="report-result-table">
              <thead className="bg-slate-50">
                <tr>
                  {columns.map(c => (
                    <th key={c} className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50" data-testid={`report-row-${i}`}>
                    {columns.map(c => (
                      <td key={c} className="px-3 py-2 text-slate-700 tabular-nums whitespace-nowrap">{formatCell(r[c])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── History list ─────────────────────────────────────────────────────────────
const HistoryPanel = ({ history, onPick }) => {
  if (!history?.length) return null;
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="pt-5">
        <h3 className="text-sm font-bold text-slate-900 mb-3 inline-flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-slate-400" /> Recent Reports
        </h3>
        <ul className="space-y-1.5">
          {history.map(h => (
            <li key={h.id}>
              <button
                type="button"
                onClick={() => onPick(h.question)}
                className="w-full text-left text-xs text-slate-600 hover:bg-slate-50 rounded-lg px-2.5 py-1.5 flex items-center gap-2"
                data-testid={`history-item-${h.id}`}
              >
                <ChevronRight className="w-3 h-3 text-slate-300 shrink-0" />
                <span className="flex-1 truncate">&ldquo;{h.question}&rdquo;</span>
                <span className="text-slate-400 text-[10px] tabular-nums shrink-0">{h.total} rows</span>
              </button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

// ── My Digests panel ─────────────────────────────────────────────────────────
const SCHEDULE_LABEL = {
  daily_weekday: 'Daily (Mon–Fri)',
  weekly_monday: 'Weekly (Mondays)',
  monthly_1st:   'Monthly (1st)',
};

const MyDigestsPanel = ({ apiPrefix, refreshKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/reports/digest${apiPrefix}`);
        if (!cancelled) setItems(r.data?.digests || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiPrefix, refreshKey, nonce]);

  const bump = () => setNonce((n) => n + 1);

  const toggle = async (d) => {
    try {
      await axios.patch(`${API}/reports/digest/${d.id}`, { active: !d.active });
      toast.success(d.active ? 'Digest paused' : 'Digest resumed');
      bump();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update digest');
    }
  };

  const remove = async (d) => {
    try {
      await axios.delete(`${API}/reports/digest/${d.id}`);
      toast.success('Digest removed');
      bump();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    }
  };

  if (loading || !items.length) return null;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="my-digests-panel">
      <CardContent className="pt-5">
        <h3 className="text-sm font-bold text-slate-900 mb-3 inline-flex items-center gap-1.5">
          <BellRing className="w-4 h-4 text-teal-500" /> Your Report Digests
          <span className="text-[11px] text-slate-500 font-normal ml-1">({items.length})</span>
        </h3>
        <ul className="divide-y divide-slate-100">
          {items.map((d) => (
            <li
              key={d.id}
              className="py-2 flex items-center gap-3"
              data-testid={`digest-item-${d.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-800 truncate">&ldquo;{d.question}&rdquo;</p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  <span className="font-semibold">{SCHEDULE_LABEL[d.schedule] || d.schedule}</span>
                  <span className="text-slate-300 mx-1.5">·</span>
                  next {d.next_run_at ? new Date(d.next_run_at).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }) : '—'}
                  {d.last_run_at && (
                    <>
                      <span className="text-slate-300 mx-1.5">·</span>
                      last {d.last_status === 'error' ? <span className="text-rose-600 font-semibold">errored</span> : 'sent'}
                    </>
                  )}
                  {!d.active && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">paused</span>}
                </p>
              </div>
              <button
                type="button"
                onClick={() => toggle(d)}
                className={`text-[11px] font-semibold px-2 py-1 rounded inline-flex items-center gap-1 ${
                  d.active ? 'text-slate-600 hover:bg-slate-100' : 'text-emerald-600 hover:bg-emerald-50'
                }`}
                data-testid={`digest-toggle-${d.id}`}
                title={d.active ? 'Pause this digest' : 'Resume this digest'}
              >
                <Power className="w-3 h-3" /> {d.active ? 'Pause' : 'Resume'}
              </button>
              <button
                type="button"
                onClick={() => remove(d)}
                className="text-[11px] font-semibold text-rose-600 hover:bg-rose-50 px-2 py-1 rounded inline-flex items-center gap-1"
                data-testid={`digest-delete-${d.id}`}
                title="Delete this digest"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};


// ── Main tab ─────────────────────────────────────────────────────────────────
/**
 * <ReportBuilderTab /> — Area 4 CDR Report Builder.
 * Type a plain-English question → LLM classifies → we execute a bounded
 * aggregation → results render as a table.
 */
const ReportBuilderTab = ({ domainId = null }) => {
  const { token } = useAuth();
  const [question, setQuestion] = useState('');
  const [examples, setExamples] = useState([]);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [digestsRefreshKey, setDigestsRefreshKey] = useState(0);

  const qs = React.useMemo(() => (domainId ? `?domain_id=${domainId}` : ''), [domainId]);

  const loadExamples = useCallback(async () => {
    try {
      const [e, h] = await Promise.all([
        axios.get(`${API}/reports/examples`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/history${qs}${qs ? '&' : '?'}limit=6`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setExamples(e.data?.examples || []);
      setHistory(h.data?.history || []);
    } catch { /* silent */ }
  }, [qs, token]);

  useEffect(() => { loadExamples(); }, [loadExamples]);

  const run = async (q) => {
    const query = (q ?? question).trim();
    if (query.length < 3) return;
    setBusy(true); setResult(null);
    try {
      const r = await axios.post(`${API}/reports/query${qs}`, { question: query }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 40000,
      });
      setResult(r.data);
      setQuestion(query);
      loadExamples(); // refresh history
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not run this report');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="report-builder-tab">
      {/* Question composer */}
      <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50 shadow-sm">
        <CardContent className="pt-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center shrink-0">
              <FileSearch className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-slate-900 font-bold text-base">CDR Report Builder</h2>
              <p className="text-[11px] text-slate-600 mt-0.5">
                Ask about calls, callers, agent performance or missed opportunities. AI classifies intent and runs the query.
              </p>
              <div className="mt-3 flex gap-2 items-center">
                <input
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !busy && run()}
                  placeholder="e.g. Which customers called us 3+ times last month?"
                  disabled={busy}
                  className="flex-1 text-sm px-3 py-2 rounded-lg border border-teal-200 bg-white focus:outline-none focus:ring-2 focus:ring-teal-400 disabled:bg-slate-50 disabled:text-slate-400"
                  data-testid="report-question-input"
                />
                <button
                  type="button"
                  disabled={busy || question.trim().length < 3}
                  onClick={() => run()}
                  className="text-sm font-semibold bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                  data-testid="report-run-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Run
                </button>
              </div>
            </div>
          </div>

          {/* Example prompt chips */}
          {examples.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5" data-testid="report-examples">
              <span className="text-[11px] text-slate-500 mr-1 inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Try:
              </span>
              {examples.map((ex, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => run(ex)}
                  className="text-[11px] px-2.5 py-1 rounded-full bg-white/70 border border-teal-200 text-teal-700 hover:bg-white transition-colors"
                  data-testid={`example-chip-${i}`}
                >
                  {ex}
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Result */}
      {busy && (
        <Card className="border-slate-200">
          <CardContent className="py-8 flex items-center justify-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Interpreting your question and running the query…
          </CardContent>
        </Card>
      )}
      {!busy && result && (
        <ResultTable
          result={result}
          apiPrefix={qs}
          onDigestSaved={() => setDigestsRefreshKey((k) => k + 1)}
        />
      )}

      {/* Saved digests */}
      <MyDigestsPanel apiPrefix={qs} refreshKey={digestsRefreshKey} />

      {/* History */}
      {history.length > 0 && <HistoryPanel history={history} onPick={run} />}
    </div>
  );
};

export default ReportBuilderTab;
